import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Headphones, Phone, Circle, RefreshCw, WifiOff } from "lucide-react";
import { toast } from "sonner";

// Live Call Monitor — supervisor-mode view of every ACTIVE call session
// in the workspace. Backed by SSE (`/api/monitor/stream`) for sub-second
// state changes. Rep click → LISTEN LIVE — dials your cell into the
// session's SignalWire conference muted. Works from state INITIATING
// onward (armed mode auto-connects when seller answers).
//
// Every browser call now flows through call_session_service (see
// backend/call_session_service.py) — no more "legacy path" errors.

const STATE_TONE = {
  INITIATING: "bg-slate-400 ring-slate-200",
  DIALING:    "bg-amber-500 ring-amber-200",
  RINGING:    "bg-amber-500 ring-amber-200 animate-pulse",
  CONNECTED:  "bg-emerald-500 ring-emerald-200",
  ENDED:      "bg-slate-400 ring-slate-100",
  FAILED:     "bg-red-500 ring-red-200",
};

const STATE_LABEL = {
  INITIATING: "Initiating…",
  DIALING:    "Dialing Seller",
  RINGING:    "Ringing",
  CONNECTED:  "LIVE",
  ENDED:      "Ended",
  FAILED:     "Failed",
};

function fmt(sec) {
  if (!sec) return "0s";
  const m = Math.floor(sec / 60), s = sec % 60;
  return m ? `${m}m ${String(s).padStart(2, "0")}s` : `${s}s`;
}

function useLiveSessions() {
  const [rows, setRows] = useState([]);
  const [connected, setConnected] = useState(false);
  const esRef = useRef(null);

  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    // EventSource can't send Authorization headers, so we thread the token
    // via query param — backend accepts either. If it doesn't, fall back
    // to polling every 2s.
    const token = localStorage.getItem("token") || "";
    const url = `${backend}/api/monitor/stream${token ? `?_token=${encodeURIComponent(token)}` : ""}`;
    try {
      const es = new EventSource(url, { withCredentials: true });
      esRef.current = es;
      es.addEventListener("sessions", (ev) => {
        try {
          const parsed = JSON.parse(ev.data);
          setRows(parsed.rows || []);
          setConnected(true);
        } catch (_e) { /* malformed frame */ }
      });
      es.onerror = () => { setConnected(false); };
      es.onopen = () => { setConnected(true); };
    } catch (_e) { setConnected(false); }
    // Polling fallback (2s) — always on; light and cheap.
    const poll = setInterval(async () => {
      try {
        const { data } = await api.get("/monitor/sessions");
        setRows(data?.rows || []);
      } catch (_e) { /* silent */ }
    }, 2000);
    return () => {
      try { esRef.current?.close(); } catch (_e) { /* noop */ }
      clearInterval(poll);
    };
  }, []);

  return { rows, connected };
}

export default function CallMonitor() {
  const { rows, connected } = useLiveSessions();
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick(x => x + 1), 1000); return () => clearInterval(t); }, []);
  // eslint-disable-next-line no-unused-vars
  const _ = tick;

  const listen = async (sess) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/monitor/join/${sess.session_id}?mode=listen`);
      if (data?.joined) {
        toast.success(data.message || "Monitoring armed");
      } else {
        toast.error(data?.message || data?.reason || "Failed to join");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Join failed");
    } finally { setBusy(false); }
  };

  const enriched = useMemo(() => rows.map((r) => {
    const startedAt = r.answered_at || r.started_at;
    const secs = startedAt ? Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000)) : 0;
    return { ...r, duration_sec: secs };
  }), [rows]);

  return (
    <div className="p-6 space-y-4" data-testid="call-monitor-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Headphones className="text-fuchsia-500" size={24} /> Live Call Monitor
          </h1>
          <div className="text-xs text-muted-foreground mt-1">
            Every active browser call. Click LISTEN LIVE to arm supervisor audio — you'll hear the conversation as soon as the seller answers.
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className={`inline-flex h-2 w-2 rounded-full ${connected ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`} />
          <span className={connected ? "text-emerald-700" : "text-slate-500"}>
            {connected ? "Real-time connected" : "Polling (SSE offline)"}
          </span>
        </div>
      </div>

      <div className="rounded-2xl border-2 border-slate-200 overflow-hidden bg-white shadow-sm">
        <div className="grid grid-cols-12 text-[10px] uppercase tracking-widest font-semibold text-slate-500 bg-slate-50 px-4 py-2.5 border-b">
          <div className="col-span-3">Rep</div>
          <div className="col-span-3">Seller / Property</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Duration</div>
          <div className="col-span-2 text-right">Action</div>
        </div>
        {enriched.length === 0 && (
          <div className="px-4 py-16 text-center text-sm text-muted-foreground">
            <WifiOff size={18} className="inline mr-1 text-slate-400" />
            No live calls right now. Sessions appear here the instant a rep clicks Dial.
          </div>
        )}
        {enriched.map((r) => {
          const tone = STATE_TONE[r.state] || STATE_TONE.INITIATING;
          const label = STATE_LABEL[r.state] || r.state;
          return (
            <div key={r.session_id}
                 className="grid grid-cols-12 items-center px-4 py-3 border-b last:border-b-0 hover:bg-slate-50 transition"
                 data-testid={`monitor-row-${r.session_id}`}>
              <div className="col-span-3">
                <div className="font-semibold text-sm truncate">{r.rep_name || r.rep_id?.slice(0, 8) || "Rep"}</div>
                <div className="text-[11px] text-muted-foreground truncate">{r.caller_id || "—"}</div>
              </div>
              <div className="col-span-3">
                <div className="font-semibold text-sm truncate">{r.seller_phone || "Unknown"}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {r.lead_id ? `Lead ${r.lead_id.slice(0, 8)}…` : "Manual dial"}
                </div>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <span className={`inline-flex h-2 w-2 rounded-full ring-4 ${tone}`} />
                <span className={`text-xs font-semibold ${r.state === "CONNECTED" ? "text-emerald-700" : "text-slate-700"}`}>
                  {label}
                </span>
              </div>
              <div className="col-span-2 text-xs font-mono">{fmt(r.duration_sec)}</div>
              <div className="col-span-2 flex items-center justify-end">
                <Button size="sm" onClick={() => listen(r)} disabled={busy}
                        className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white h-7 text-[11px]"
                        data-testid={`monitor-listen-${r.session_id}`}>
                  <Headphones size={12} className="mr-1" /> LISTEN LIVE
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
